/**
 * @fileoverview Own the searchable audit of recorded Slack attempts and their requested/actual destinations. Backend logs are delivery evidence; employee/channel catalogs enrich recipient identity without changing recorded targets. Explicit test send is a separate mutation from browsing. `kc-internal-backend/integrations/slack/client.py` owns delivery behavior. Source labels and displayed payloads do not prove successful receipt beyond the stored provider acknowledgement.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import api from '../../api'
import ToastContext from '../../ToastContext'
import { AppBadge, AppButton, AppDataTable, AppColumn, AppModal, AppSelect, AppTextInput } from '../shared'
import Avatar from '../Avatar/Avatar'
import { AdminCard, AdminEmptyState, AdminPage, AdminSection } from './AdminChrome'

const DEFAULT_FILTERS = {
    environment: '',
    status: '',
    source: '',
    destinationType: '',
    search: '',
    startDate: '',
    endDate: '',
}

const FIELD_GRID_STYLE = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '1rem',
}

const DETAIL_GRID_STYLE = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '0.75rem',
}

const PREVIEW_STYLE = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '28rem',
}



function SlackMessages({ embedded = false }) {
    const showToast = useContext(ToastContext)
    const [messages, setMessages] = useState([])
    const [loading, setLoading] = useState(false)
    const [sendingTestMessage, setSendingTestMessage] = useState(false)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(25)
    const [total, setTotal] = useState(0)
    const [filters, setFilters] = useState(DEFAULT_FILTERS)
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS)
    const [selectedMessage, setSelectedMessage] = useState(null)
    const [testRecipientSlackId, setTestRecipientSlackId] = useState('')
    const [slackChannels, setSlackChannels] = useState([])
    const [employees, setEmployees] = useState([])

    const fetchMessages = useCallback(

    async (showRefreshedToast = false) => {
        setLoading(true)
        try {
            const response = await api.get('/slack_messages', {
                params: buildQueryParams({ page, pageSize, filters: appliedFilters }),
            })
            setMessages(response.data?.messages || [])
            setTotal(response.data?.total || 0)
            if (showRefreshedToast) {
                showToast({ severity: 'info', summary: 'Refreshed', detail: 'Slack messages refreshed' })
            }
        } catch (error) {
            console.error('Failed to fetch Slack messages:', error)
            if (showRefreshedToast) {
                showToast({ severity: 'error', summary: 'Error', detail: 'Slack messages not refreshed' })
            }
        } finally {
            setLoading(false)
        }
    }, [appliedFilters, page, pageSize, showToast])

    const fetchSupportData = useCallback(

    async () => {
        try {
            const [channelResponse, employeeResponse] = await Promise.all([
                api.get('/automations/slack-channels'),
                api.get('/employees'),
            ])
            setSlackChannels(channelResponse.data?.allChannels || [])
            setEmployees(Array.isArray(employeeResponse.data) ? employeeResponse.data : [])
        } catch (error) {
            console.error('Failed to fetch Slack support data:', error)
        }
    }, [])

    useEffect(() => {
        fetchSupportData()
    }, [fetchSupportData])

    useEffect(() => {
        fetchMessages(false)
    }, [fetchMessages])

    const channelLabelById = useMemo(() => {
        const nextMap = {}
        for (const channel of slackChannels) {
            if (channel?.id) {
                nextMap[channel.id] = channel.name || channel.id
            }
        }
        return nextMap
    }, [slackChannels])

    const employeeLabelBySlackId = useMemo(() => {
        const nextMap = {}
        for (const employee of employees) {
            if (employee?.SlackUserId) {
                nextMap[employee.SlackUserId] = employee.Name || employee.Email || employee.SlackUserId
            }
        }
        return nextMap
    }, [employees])

    const employeeProfileBySlackId = useMemo(() => {
        const nextMap = {}
        for (const employee of employees) {
            if (employee?.SlackUserId) {
                nextMap[employee.SlackUserId] = employee.ProfilePic || null
            }
        }
        return nextMap
    }, [employees])

    const testRecipientOptions = useMemo(() => {
        return employees
            .filter((employee) => employee?.SlackUserId)
            .sort((left, right) => {
                const leftName = `${left?.Name || ''}`.toLowerCase()
                const rightName = `${right?.Name || ''}`.toLowerCase()
                return leftName.localeCompare(rightName)
            })
            .map((employee) => ({
                label: employee.Email ? `${employee.Name || employee.SlackUserId} (${employee.Email})` : (employee.Name || employee.SlackUserId),
                value: employee.SlackUserId,
            }))
    }, [employees])

    const sourcePresentationByKey = useMemo(() => buildSourcePresentation(messages), [messages])

    const totalPages = Math.max(1, Math.ceil(total / pageSize))
    const rangeStart = total === 0 ? 0 : ((page - 1) * pageSize) + 1
    const rangeEnd = Math.min(page * pageSize, total)

    const Wrapper = embedded ? AdminSection : AdminPage

    const handleSendTestMessage = useCallback(

    async () => {
        if (!testRecipientSlackId) return

        setSendingTestMessage(true)
        try {
            const response = await api.post('/slack_messages/test', {
                slack_user_id: testRecipientSlackId,
            })
            showToast({
                severity: 'success',
                summary: 'Sent',
                detail: `Test message sent to ${response.data?.recipient_label || 'selected user'}`,
            })
            setFilters({ ...DEFAULT_FILTERS })
            setAppliedFilters({ ...DEFAULT_FILTERS })
            setPage(1)
        } catch (error) {
            console.error('Failed to send Slack test message:', error)
            showToast({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.error || 'Slack test message not sent',
            })
        } finally {
            setSendingTestMessage(false)
        }
    }, [showToast, testRecipientSlackId])

    return (
        <Wrapper
            title="Slack Messages"
            description="Review outbound Slack messages sent by this backend across development, staging, and production."
            actions={(
                <AppButton icon="pi pi-refresh" variant="info" onClick={() => fetchMessages(true)} label="Refresh" />
            )}
        >
            <AdminCard
                title="Send Test Message"
                description="Send a real test DM through the shared Slack client and record it in the feed below."
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem', alignItems: 'end' }}>
                    <Field label="Recipient">
                        <AppSelect
                            value={testRecipientSlackId}
                            onChange={setTestRecipientSlackId}
                            options={testRecipientOptions}
                            placeholder={testRecipientOptions.length ? 'Select a Slack user' : 'No Slack users available'}
                            disabled={!testRecipientOptions.length || sendingTestMessage}
                        />
                    </Field>
                    <AppButton
                        icon="pi pi-send"
                        variant="primary"
                        label={sendingTestMessage ? 'Sending...' : 'Send Test Message'}
                        onClick={handleSendTestMessage}
                        disabled={!testRecipientSlackId || sendingTestMessage}
                        style={{ alignSelf: 'end', justifySelf: 'start' }}
                    />
                </div>
            </AdminCard>

            <AdminCard
                title="Filters"
                description="Filter by environment, source, destination, status, or message content."
            >
                <div style={FIELD_GRID_STYLE}>
                    <Field label="Search">
                        <AppTextInput
                            value={filters.search}
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                            placeholder="Text, target, type, metadata"
                        />
                    </Field>
                    <Field label="Environment">
                        <AppSelect
                            value={filters.environment}
                            onChange={(value) => setFilters(prev => ({ ...prev, environment: value }))}
                            options={ENVIRONMENT_OPTIONS}
                            placeholder="All environments"
                        />
                    </Field>
                    <Field label="Status">
                        <AppSelect
                            value={filters.status}
                            onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                            options={STATUS_OPTIONS}
                            placeholder="All statuses"
                        />
                    </Field>
                    <Field label="Source">
                        <AppSelect
                            value={filters.source}
                            onChange={(value) => setFilters(prev => ({ ...prev, source: value }))}
                            options={SOURCE_OPTIONS}
                            placeholder="All sources"
                        />
                    </Field>
                    <Field label="Destination Type">
                        <AppSelect
                            value={filters.destinationType}
                            onChange={(value) => setFilters(prev => ({ ...prev, destinationType: value }))}
                            options={DESTINATION_OPTIONS}
                            placeholder="All destinations"
                        />
                    </Field>
                    <Field label="Start Date">
                        <AppTextInput
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                        />
                    </Field>
                    <Field label="End Date">
                        <AppTextInput
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                        />
                    </Field>
                    <Field label="Page Size">
                        <AppSelect
                            value={String(pageSize)}
                            onChange={(value) => {
                                setPageSize(Number(value))
                                setPage(1)
                            }}
                            options={PAGE_SIZE_OPTIONS}
                        />
                    </Field>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '1rem' }}>
                    <AppButton
                        icon="pi pi-filter"
                        variant="primary"
                        label="Apply Filters"
                        onClick={() => {
                            setAppliedFilters(filters)
                            setPage(1)
                        }}
                    />
                    <AppButton
                        icon="pi pi-times"
                        variant="secondary"
                        label="Clear"
                        onClick={() => {
                            setFilters(DEFAULT_FILTERS)
                            setAppliedFilters(DEFAULT_FILTERS)
                            setPage(1)
                        }}
                    />
                </div>
            </AdminCard>

            <AdminCard
                title="Feed"
                description="Newest messages first. Open a row to inspect the full text and stored Block Kit payload."
            >
                {messages.length > 0 ? (
                    <>
                        <AppDataTable
                            value={messages}
                            loading={loading}
                            showGridlines
                            stripedRows
                            responsiveLayout="scroll"
                            tableStyle={{ minWidth: '95rem' }}
                            emptyMessage="No Slack messages found"
                            onRowClick={(event) => setSelectedMessage(event.data)}
                        >
                            <AppColumn field="created_at" header="Timestamp" body={(row) => formatTimestamp(row.created_at)} style={{ width: '13%' }} />
                            <AppColumn field="environment" header="Environment" body={(row) => <AppBadge value={row.environment} severity={environmentSeverity(row.environment)} />} style={{ width: '8%' }} />
                            <AppColumn field="source" header="Source" body={(row) => (
                                <SourceBadge source={row.source} sourcePresentationByKey={sourcePresentationByKey} />
                            )} style={{ width: '10%' }} />
                            <AppColumn field="message_type" header="Type" body={(row) => <span>{row.message_type}</span>} style={{ width: '14%' }} />
                            <AppColumn field="status" header="Status" body={(row) => <AppBadge value={row.status} severity={row.status === 'success' ? 'success' : 'danger'} />} style={{ width: '8%' }} />
                            <AppColumn field="destination" header="Destination" body={(row) => (
                                <DestinationCell
                                    row={row}
                                    channelLabelById={channelLabelById}
                                    employeeLabelBySlackId={employeeLabelBySlackId}
                                    employeeProfileBySlackId={employeeProfileBySlackId}
                                />
                            )} style={{ width: '17%' }} />
                            <AppColumn field="text" header="Preview" body={(row) => <div style={PREVIEW_STYLE}>{row.text || '[No text]'}</div>} style={{ width: '24%' }} />
                            <AppColumn field="actions" header="Actions" body={(row) => (
                                <AppButton
                                    label="View"
                                    variant="secondary"
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        setSelectedMessage(row)
                                    }}
                                />
                            )} style={{ width: '6%' }} />
                        </AppDataTable>

                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center', marginTop: '1rem' }}>
                            <div style={{ color: 'var(--text-color-secondary)', fontSize: '0.9rem' }}>
                                Showing {rangeStart}-{rangeEnd} of {total}
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                <span style={{ color: 'var(--text-color-secondary)', fontSize: '0.9rem' }}>
                                    Page {page} of {totalPages}
                                </span>
                                <AppButton
                                    label="Previous"
                                    variant="secondary"
                                    disabled={page <= 1 || loading}
                                    onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                                />
                                <AppButton
                                    label="Next"
                                    variant="secondary"
                                    disabled={page >= totalPages || loading}
                                    onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    <AdminEmptyState
                        title="No Slack messages yet"
                        description="Outbound Slack traffic will appear here after the backend sends messages through the shared Slack client."
                    />
                )}
            </AdminCard>

            <SlackMessageDetailModal
                message={selectedMessage}
                onClose={() => setSelectedMessage(null)}
                channelLabelById={channelLabelById}
                employeeLabelBySlackId={employeeLabelBySlackId}
                employeeProfileBySlackId={employeeProfileBySlackId}
                sourcePresentationByKey={sourcePresentationByKey}
            />
        </Wrapper>
    )
}



function Field({ label, children }) {
    return (
        <div>
            <div style={{ marginBottom: '0.4rem', fontWeight: 600, color: 'var(--text-color)' }}>{label}</div>
            {children}
        </div>
    )
}



function DestinationCell({ row, channelLabelById, employeeLabelBySlackId, employeeProfileBySlackId }) {
    const recipient = resolveRecipientPresentation(row, channelLabelById, employeeLabelBySlackId, employeeProfileBySlackId)
    return (
        <RecipientIdentity recipient={recipient} showTargetId />
    )
}



function SourceBadge({ source, sourcePresentationByKey }) {
    const presentation = sourcePresentationByKey[source] || buildSourceBadgePresentation(source, 0)
    return (
        <AppBadge
            value={presentation.label}
            severity="secondary"
            style={presentation.style}
        />
    )
}



function RecipientIdentity({ recipient, showTargetId = false, size = 28 }) {
    if (!recipient) return '—'

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
            <Avatar
                src={recipient.profilePic}
                name={recipient.label}
                size={size}
                style={{ flexShrink: 0 }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', minWidth: 0 }}>
                <span style={{ color: 'var(--text-color)', wordBreak: 'break-word' }}>{recipient.label}</span>
                {showTargetId ? (
                    <span style={{ color: 'var(--text-color-secondary)', fontSize: '0.8rem', wordBreak: 'break-word' }}>
                        {recipient.targetId}
                    </span>
                ) : null}
            </div>
        </div>
    )
}



function SlackMessageDetailModal({ message, onClose, channelLabelById, employeeLabelBySlackId, employeeProfileBySlackId, sourcePresentationByKey }) {
    if (!message) return null

    const metadata = parseJson(message.metadata)
    const blocks = parseJson(message.blocks)
    const recipient = resolveRecipientPresentation(message, channelLabelById, employeeLabelBySlackId, employeeProfileBySlackId)
    const sourcePresentation = sourcePresentationByKey[message.source] || buildSourceBadgePresentation(message.source, 0)

    return (
        <AppModal
            visible={Boolean(message)}
            title="Slack Message Details"
            onClose={onClose}
            width="min(92vw, 960px)"
            footer={<AppButton label="Close" variant="secondary" onClick={onClose} />}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div style={DETAIL_GRID_STYLE}>
                    <DetailField label="Timestamp" value={formatTimestamp(message.created_at)} />
                    <DetailField label="Updated" value={formatTimestamp(message.updated_at)} />
                    <DetailField label="Environment" value={message.environment} />
                    <DetailField label="Status" value={message.status} />
                    <DetailField label="Source" value={sourcePresentation.label} />
                    <DetailField label="Message Type" value={message.message_type} />
                    <DetailField label="Destination Type" value={message.destination_type} />
                    <DetailField label="Delivery Mode" value={message.delivery_mode} />
                    <DetailField label="Destination" value={<RecipientIdentity recipient={recipient} size={32} />} />
                    <DetailField label="Requested Target" value={message.requested_target_id} />
                    <DetailField label="Actual Target" value={message.actual_target_id} />
                    <DetailField label="Slack Timestamp" value={message.slack_message_ts} />
                    <DetailField label="Error Code" value={message.error_code} />
                </div>

                <Section title="Text">
                    <pre style={preStyle}>{message.text || '[No text stored]'}</pre>
                </Section>

                <Section title="Blocks JSON">
                    <pre style={preStyle}>{formatJson(blocks, message.blocks)}</pre>
                </Section>

                <Section title="Metadata JSON">
                    <pre style={preStyle}>{formatJson(metadata, message.metadata)}</pre>
                </Section>

                {message.error_message ? (
                    <Section title="Error Message">
                        <pre style={preStyle}>{message.error_message}</pre>
                    </Section>
                ) : null}
            </div>
        </AppModal>
    )
}



function DetailField({ label, value }) {
    return (
        <div style={{ border: '1px solid var(--surface-border)', borderRadius: 'var(--admin-radius-sm)', padding: '0.8rem', backgroundColor: 'var(--admin-muted-bg)' }}>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-color-secondary)' }}>{label}</div>
            <div style={{ marginTop: '0.35rem', color: 'var(--text-color)', wordBreak: 'break-word' }}>{value || '—'}</div>
        </div>
    )
}



function Section({ title, children }) {
    return (
        <div>
            <div style={{ marginBottom: '0.5rem', fontWeight: 600, color: 'var(--text-color)' }}>{title}</div>
            {children}
        </div>
    )
}



function parseJson(raw) {
    if (!raw) return null
    if (typeof raw === 'object') return raw
    try {
        return JSON.parse(raw)
    } catch {
        return null
    }
}



function formatJson(parsedValue, fallbackRaw) {
    if (parsedValue) {
        return JSON.stringify(parsedValue, null, 2)
    }
    return fallbackRaw || '[No data stored]'
}



function resolveTargetLabel(row, channelLabelById, employeeLabelBySlackId) {
    const metadata = parseJson(row.metadata)
    if (metadata?.recipient_label) {
        return metadata.recipient_label
    }

    const targetIds = [row.requested_target_id, row.actual_target_id]
    for (const id of targetIds) {
        if (!id) continue
        if (channelLabelById[id]) return channelLabelById[id]
        if (employeeLabelBySlackId[id]) return employeeLabelBySlackId[id]
    }

    return row.actual_target_id || row.requested_target_id || 'Unknown target'
}



function resolveRecipientPresentation(row, channelLabelById, employeeLabelBySlackId, employeeProfileBySlackId) {
    const targetIds = [row.requested_target_id, row.actual_target_id]
    let profilePic = null

    for (const id of targetIds) {
        if (!id) continue
        if (employeeProfileBySlackId[id]) {
            profilePic = employeeProfileBySlackId[id]
            break
        }
    }

    return {
        label: resolveTargetLabel(row, channelLabelById, employeeLabelBySlackId),
        targetId: row.actual_target_id || row.requested_target_id || 'Unknown target',
        profilePic,
    }
}



function buildSourcePresentation(messages) {
    const seenSources = new Set(SOURCE_OPTIONS.map((option) => option.value))
    for (const message of messages) {
        if (message?.source) {
            seenSources.add(message.source)
        }
    }

    const orderedSources = Array.from(seenSources)
        .filter(Boolean)
        .sort(compareSourceKeys)

    const presentationByKey = {}
    for (const [index, source] of orderedSources.entries()) {
        presentationByKey[source] = buildSourceBadgePresentation(source, index)
    }
    return presentationByKey
}



function compareSourceKeys(left, right) {
    const leftIndex = SOURCE_ORDER_INDEX[left]
    const rightIndex = SOURCE_ORDER_INDEX[right]
    const leftKnown = leftIndex !== undefined
    const rightKnown = rightIndex !== undefined

    if (leftKnown && rightKnown) return leftIndex - rightIndex
    if (leftKnown) return -1
    if (rightKnown) return 1

    return left.localeCompare(right)
}



function buildSourceBadgePresentation(source, index) {
    const palette = SOURCE_BADGE_PALETTE[index % SOURCE_BADGE_PALETTE.length]
    return {
        label: resolveSourceLabel(source),
        style: {
            backgroundColor: palette.backgroundColor,
            color: palette.color,
        },
    }
}



function resolveSourceLabel(source) {
    if (!source) return 'Unknown'
    return SOURCE_LABELS_BY_VALUE[source] || humanizeSourceKey(source)
}



function humanizeSourceKey(source) {
    return source
        .split(/[_-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
}



function buildQueryParams({ page, pageSize, filters }) {
    const params = {
        page,
        page_size: pageSize,
    }
    if (filters.environment) params.environment = filters.environment
    if (filters.status) params.status = filters.status
    if (filters.source) params.source = filters.source
    if (filters.destinationType) params.destination_type = filters.destinationType
    if (filters.search) params.search = filters.search
    if (filters.startDate) params.start_date = filters.startDate
    if (filters.endDate) params.end_date = filters.endDate
    return params
}



function formatTimestamp(value) {
    if (!value) return '—'
    return new Date(value).toLocaleString()
}



function environmentSeverity(environment) {
    if (environment === 'production') return 'danger'
    if (environment === 'staging') return 'warning'
    return 'info'
}

const preStyle = {
    margin: 0,
    padding: '0.9rem 1rem',
    borderRadius: 'var(--admin-radius-sm)',
    border: '1px solid var(--surface-border)',
    backgroundColor: 'var(--admin-muted-bg)',
    color: 'var(--text-color)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '18rem',
    overflow: 'auto',
    fontSize: '0.85rem',
    lineHeight: 1.6,
}

const PAGE_SIZE_OPTIONS = [
    { label: '25', value: '25' },
    { label: '50', value: '50' },
    { label: '100', value: '100' },
]

const ENVIRONMENT_OPTIONS = [
    { label: 'Development', value: 'development' },
    { label: 'Staging', value: 'staging' },
    { label: 'Production', value: 'production' },
]

const STATUS_OPTIONS = [
    { label: 'Success', value: 'success' },
    { label: 'Error', value: 'error' },
]

const DESTINATION_OPTIONS = [
    { label: 'DM', value: 'dm' },
    { label: 'Channel', value: 'channel' },
    { label: 'Unknown', value: 'unknown' },
]

const SOURCE_OPTIONS = [
    { label: 'AI Calendar', value: 'ai_calendar' },
    { label: 'Notification Rules', value: 'notification_rules' },
    { label: 'Banking', value: 'banking' },
    { label: 'Client Won', value: 'client_won' },
    { label: 'Trello', value: 'trello' },
    { label: 'Billing', value: 'billing' },
    { label: 'Slack Messages Test', value: 'slack_messages_test' },
    { label: 'System Error', value: 'system_error' },
    { label: 'Unknown', value: 'unknown' },
]

const SOURCE_BADGE_PALETTE = [
    { backgroundColor: '#0f766e', color: '#ffffff' },
    { backgroundColor: '#1d4ed8', color: '#ffffff' },
    { backgroundColor: '#7c3aed', color: '#ffffff' },
    { backgroundColor: '#b45309', color: '#ffffff' },
    { backgroundColor: '#be123c', color: '#ffffff' },
    { backgroundColor: '#4338ca', color: '#ffffff' },
    { backgroundColor: '#166534', color: '#ffffff' },
    { backgroundColor: '#334155', color: '#ffffff' },
]

const SOURCE_LABELS_BY_VALUE = SOURCE_OPTIONS.reduce((accumulator, option) => {
    accumulator[option.value] = option.label
    return accumulator
}, {})

const SOURCE_ORDER_INDEX = SOURCE_OPTIONS.reduce((accumulator, option, index) => {
    accumulator[option.value] = index
    return accumulator
}, {})

export default SlackMessages
